import { FilterPlayIcon, FilterStopIcon, InfoIcon } from '@/components/Icons';
import { ScrollArea } from '@/components/ui/scroll-area';
import { FilterGroup } from '@/service/type/Filter';
import { useAudioPlayerStore } from '@/state/audio-player-store';
import { useGeneratorFilterStore } from '@/state/generator-filter-store';
import { cn } from '@/utils';
import useFlags from '@jenmusic/feature-flag';
import { useRef } from 'react';
import { CircularProgressWithPause } from '../StyleFilterList';
import { getFilterBalance } from '../../../utils/getFilterBalance';

interface StyleFilterListProps {
  defaultCurrentFilterGroup?: FilterGroup;
  onSelect?: (filterGroup: FilterGroup) => void;
}

const StyleFilterList: React.FC<StyleFilterListProps> = ({ onSelect }) => {
  const { showShopFilters } = useFlags();
  const { filterGroupList } = useGeneratorFilterStore();

  const containerRef = useRef<HTMLDivElement>(null);

  return (
    <div className="w-full h-full border-light-top rounded">
      <div
        className="w-full h-full min-h-[371px] rounded"
        style={{
          border: '3px solid var(--_Lil-Bottom-Highlight, rgba(255, 255, 255, 0.00))',
          background: 'linear-gradient(180deg,#333537, #2B2E30)',
          backgroundOrigin: 'border-box',
          backgroundRepeat: 'no-repeat',
        }}
      >
        <div className="w-full h-full bg-[#191a1b] rounded-sm flex flex-col">
          {showShopFilters && (
            <>
              <div className="p-[16px]">
                <div
                  className="p-[6px] bg-[#efeff0] text-[#191a1b] text-[12px] rounded w-fit flex gap-[4px] hover:bg-[#efeff0]/80 cursor-pointer"
                  onClick={() => {
                    window.open('/marketplace');
                  }}
                >
                  <span>SHOP</span>
                  <span>FILTERS</span>
                </div>
              </div>
            </>
          )}
          <div className="border-b border-[#48484b]"></div>
          <div className={cn('w-full h-full bg-[#131415] text-white px-1 pt-[14px]')}>
            <div ref={containerRef} className="hidden" />
            <ScrollArea className={cn(' h-full overflow-auto px-1')}>
              {(filterGroupList ?? []).map((filter, i) => (
                <StyleFilterListItem key={i} filterGroup={filter} onSelect={onSelect} />
              ))}
            </ScrollArea>
          </div>
        </div>
      </div>
    </div>
  );
};

const StyleFilterListItem = ({ filterGroup, onSelect }: { filterGroup: FilterGroup; onSelect?: (filterGroup: FilterGroup) => void }) => {
  const { currentFilterGroup, currentStrength } = useGeneratorFilterStore();
  const { play, playAudioInfo, isPlaying, pause } = useAudioPlayerStore();

  const handlePlay = (filterGroup: FilterGroup) => {
    if (isPlaying && playAudioInfo?.id === String(filterGroup.id)) {
      pause();
    } else {
      play({
        url: filterGroup.audio,
        name: filterGroup.name,
        id: String(filterGroup.id),
      });
    }
  };

  const balanceData = getFilterBalance({
    filter: filterGroup,
    currentStrength,
    isSelected: currentFilterGroup?.id === filterGroup.id,
  });

  const balance = balanceData.balance;
  const hasWarning = balanceData.hasWarning;

  return (
    <div
      onClick={() => {
        onSelect?.(filterGroup);
      }}
      className={cn(
        'flex items-center gap-3 group cursor-pointer hover:bg-[#1c1c1d] rounded p-2',
        currentFilterGroup?.id === filterGroup.id && 'text-jen-orange',
      )}
    >
      <div
        className={cn(
          'w-4 h-4 flex items-center justify-center',
          currentFilterGroup?.id === filterGroup.id ? 'text-jen-orange' : 'text-white/60',
        )}
        onClick={(e) => {
          e.stopPropagation();
          handlePlay(filterGroup);
        }}
      >
        {isPlaying && playAudioInfo?.id === String(filterGroup.id) ? <CircularProgressWithPause /> : <FilterPlayIcon />}
      </div>
      <div className="relative shrink-0">
        {(filterGroup.thumb_album_cover ?? filterGroup.album_cover) ? (
          <img
            src={filterGroup.thumb_album_cover ?? filterGroup.album_cover}
            className={cn('w-[40px] h-[60px] block rounded object-cover bg-cover flex-shrink-0 bg-neutral-800')}
          />
        ) : (
          <div className={cn('w-[40px] h-[60px] block rounded object-cover bg-cover flex-shrink-0 bg-jen-orange')} />
        )}
      </div>

      <div className={cn(' w-[60px] font-pressura-mono shrink-0')}>
        <div className="text-[0.75rem] tracking-wide w-full uppercase line-clamp-1 overflow-hidden">{filterGroup.artist}</div>
        <div className="text-[0.75rem] tracking-wide w-full uppercase line-clamp-1 overflow-hidden">{filterGroup.name}</div>
      </div>

      <div
        className={cn(
          'text-[0.75rem] font-pressura-mono uppercase w-[58px] shrink-0',
          currentFilterGroup?.id === filterGroup.id ? 'text-jen-orange' : 'text-[#9d9da6]',
        )}
      >
        {hasWarning && <InfoIcon />}
        {balance > 0 ? `${balance} mins` : 'unlimited mins'}
      </div>
    </div>
  );
};

export default StyleFilterList;
